import { FC, useState } from 'react';
import { ProductCostCardProps, ProductCostSectionProps } from './ProductCostCard.types';
import {
  Container,
  InfoContainer,
  TopRow,
  ProductInfo,
  IconContainer,
  Divider,
  MetricsRow,
  MetricColumn,
  ImpactColumn,
  ImpactContainer,
  ImpactIcon,
  EmptyState,
  AllieTextProduced,
  ProductInfoName,
  SectionContainer,
  SeeMoreButton,
} from './ProductCostCard.styles';
import { AllieText } from '../../../../../components';
import {
  AllieCubeOutlineIcon,
  AllieTrendingDownOutlineIcon,
  AllieTrendingUpOutlineIcon,
  AllieCaretDownOutlineIcon,
  AllieCaretUpOutlineIcon,
} from '../../../../../styling/icons/Icons';
import { numberFormatWithCommas } from '../../../../../common/utils';

const ProductCostCard: FC<ProductCostCardProps> = ({ data, labels, isEmpty = false }) => {
  if (isEmpty) {
    return (
      <Container>
        <EmptyState></EmptyState>
      </Container>
    );
  }

  const formatNumber = (number: number) => {
    return numberFormatWithCommas(number.toFixed(2));
  };

  const isPositiveImpact = data.impact <= 0;

  return (
    <Container>
      <InfoContainer>
        <TopRow>
          <ProductInfo>
            <ProductInfoName>
              <IconContainer>
                <AllieCubeOutlineIcon />
              </IconContainer>
              <AllieText type="body2" weight={600} color="neutral-70">
                {data.name}
              </AllieText>
            </ProductInfoName>
            <AllieTextProduced type="body2" weight={400}>
              {formatNumber(data.finishedProduct)} {labels.producedLabel}
            </AllieTextProduced>
          </ProductInfo>
        </TopRow>

        <Divider />

        <MetricsRow>
          <MetricColumn>
            <AllieText type="body3" weight={600} color="neutral-70">
              {labels.totalPlannedCost}
            </AllieText>
            <AllieText type="body3" color="neutral-70">
              {`$${formatNumber(data.totalPlannedCost)}`}
            </AllieText>
          </MetricColumn>

          <MetricColumn>
            <AllieText type="body3" weight={600} color="neutral-70">
              {labels.totalActualCost}
            </AllieText>
            <AllieText type="body3" color="neutral-70">
              {`$${formatNumber(data.totalActualCost)}`}
            </AllieText>
          </MetricColumn>

          <MetricColumn>
            <AllieText type="body3" weight={600} color="neutral-70">
              {labels.plannedUnitCost}
            </AllieText>
            <AllieText type="body3" color="neutral-70">
              {`$${formatNumber(data.plannedUnitCost)}`}
            </AllieText>
          </MetricColumn>

          <MetricColumn>
            <AllieText type="body3" weight={600} color="neutral-70">
              {labels.actualUnitCost}
            </AllieText>
            <AllieText type="body3" color="neutral-70">
              {`$${formatNumber(data.actualUnitCost)}`}
            </AllieText>
          </MetricColumn>

          <ImpactColumn>
            <AllieText type="body3" weight={600} color="neutral-70">
              {labels.impact}
            </AllieText>
            <ImpactContainer>
              <AllieText
                type="body3"
                weight={600}
                color={isPositiveImpact ? 'success-50' : 'error-50'}
              >
                {`$${formatNumber(Math.abs(data.impact))}`}
              </AllieText>
              <ImpactIcon>
                {isPositiveImpact ? (
                  <AllieTrendingUpOutlineIcon color="success" />
                ) : (
                  <AllieTrendingDownOutlineIcon color="danger" />
                )}
              </ImpactIcon>
            </ImpactContainer>
          </ImpactColumn>
        </MetricsRow>
      </InfoContainer>
    </Container>
  );
};

export const ProductCostSection: FC<ProductCostSectionProps> = ({
  costByProduct,
  labels,
  isEmpty = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const visibleProducts = expanded ? costByProduct : costByProduct.slice(0, 3);
  const shouldShowToggle = costByProduct.length > 3;

  return (
    <SectionContainer>
      <AllieText type="body1" weight={500}>
        {labels.title}
      </AllieText>
      {visibleProducts.map((productCost, index) => (
        <ProductCostCard
          key={`${productCost.name}-${index}`}
          data={productCost}
          labels={labels}
          isEmpty={isEmpty}
        />
      ))}
      {shouldShowToggle && (
        <SeeMoreButton onClick={handleToggle}>
          {expanded ? (
            <>
              {labels.seeLess}
              <AllieCaretUpOutlineIcon slot='end' />
            </>
          ) : (
            <>
              {labels.seeMore}
              <AllieCaretDownOutlineIcon slot='end' />
            </>
          )}
        </SeeMoreButton>
      )}
    </SectionContainer>
  );
};
